/**
 * Generated Lead Card Component (Compact View)
 * Displays minimal lead information with "View Details" action
 * Part of Lead Generation Redesign
 */

import React from 'react';
import {
  Card,
  CardContent,
  CardActions,
  Typography,
  Box,
  Chip,
  Button,
  Avatar,
  Stack,
  Tooltip,
  IconButton,
  useTheme,
  useMediaQuery,
  LinearProgress,
} from '@mui/material';
import {
  Business as BusinessIcon,
  LocationOn as LocationIcon,
  Star as StarIcon,
  OpenInNew as OpenInNewIcon,
  Visibility as ViewIcon,
  Add as AddIcon,
  Verified as VerifiedIcon,
  ThumbDown as ThumbDownIcon,
  People as PeopleIcon,
  Refresh as RetryIcon,
} from '@mui/icons-material';
import type { GeneratedLead } from '../types';
import AssignLeadMenu from './AssignLeadMenu';

interface GeneratedLeadCardProps {
  lead: GeneratedLead;
  onViewDetails: (lead: GeneratedLead) => void;
  onQuickAdd?: (lead: GeneratedLead) => void;
  onRetryEnrich?: (lead: GeneratedLead) => void;
  isLoading?: boolean;
}

// Skeleton loader for loading state
const SkeletonCard: React.FC = () => (
  <Card
    sx={{
      mb: 2,
      border: 1,
      borderColor: 'divider',
      overflow: 'hidden',
      position: 'relative',
      '&::before': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: '-150%',
        height: '100%',
        width: '150%',
        background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.4), transparent)',
        animation: 'shimmer 2s infinite',
      },
      '@keyframes shimmer': {
        '0%': { left: '-150%' },
        '100%': { left: '150%' }
      }
    }}
  >
    <CardContent sx={{ pb: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <Box sx={{ width: 48, height: 48, borderRadius: '50%', bgcolor: 'action.hover' }} />
        <Box sx={{ flex: 1 }}>
          <Box sx={{ height: 24, width: '60%', bgcolor: 'action.hover', borderRadius: 1, mb: 1 }} />
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Box sx={{ height: 20, width: 80, bgcolor: 'action.hover', borderRadius: 12 }} />
            <Box sx={{ height: 20, width: 100, bgcolor: 'action.hover', borderRadius: 12 }} />
          </Box>
        </Box>
        <Box sx={{ height: 32, width: 80, bgcolor: 'action.hover', borderRadius: 16 }} />
      </Box>
    </CardContent>
    <CardActions sx={{ px: 2, pb: 2, pt: 0 }}>
      <Box sx={{ height: 36, width: '100%', bgcolor: 'action.hover', borderRadius: 1 }} />
    </CardActions>
  </Card>
);

const GeneratedLeadCard: React.FC<GeneratedLeadCardProps> = ({
  lead,
  onViewDetails,
  onQuickAdd,
  onRetryEnrich,
  isLoading = false,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  if (isLoading) {
    return <SkeletonCard />;
  }

  // Filter out generic/non-person contacts — same logic as LeadDetailModal
  const genericNames = new Set([
    'general enquiries', 'general enquiry', 'general contact', 'sales team',
    'business development', 'hr department', 'info', 'contact', 'support',
    'customer service', 'reception', 'admin', 'office', 'marketing team',
  ]);
  const isRealPerson = (name: string) => {
    if (!name) return false;
    const lower = name.toLowerCase().trim();
    if (genericNames.has(lower)) return false;
    if (lower.split(/\s+/).length < 2) return false;
    if (/\b(team|department|enquir|contact|support|office|general)\b/i.test(lower)) return false;
    return true;
  };

  // Count valid contacts (real person name + position)
  const getContactCount = (): number => {
    const meta = lead.metadata as any;
    if (meta?.contacts && Array.isArray(meta.contacts) && meta.contacts.length > 0) {
      return meta.contacts.filter((c: any) => isRealPerson(c.contactName) && c.contactPosition).length;
    }
    if (isRealPerson(lead.contactName || '') && lead.contactPosition) return 1;
    return 0;
  };
  const contactCount = getContactCount();

  const getPotentialColor = (rating?: string) => {
    switch (rating) {
      case 'VERY_HIGH': return 'success';
      case 'HIGH': return 'info';
      case 'MEDIUM': return 'warning';
      case 'LOW': return 'error';
      default: return 'default';
    }
  };

  const getScoreColor = (score?: number) => {
    if (!score) return 'text.secondary';
    if (score >= 80) return 'success.main';
    if (score >= 60) return 'info.main';
    if (score >= 40) return 'warning.main';
    return 'error.main';
  };

  const getLegitimacyBadge = () => {
    if (lead.legitimacyStatus === 'CONVERTED') {
      return (
        <Tooltip title={`Proven lead - ${lead.timesConverted} conversions`}>
          <Chip
            icon={<VerifiedIcon />}
            label="Proven"
            color="success"
            size="small"
            sx={{ ml: 1 }}
          />
        </Tooltip>
      );
    }
    if (lead.legitimacyStatus === 'VERIFIED') {
      return (
        <Tooltip title={`Verified lead - Selected ${lead.timesSelected} times`}>
          <Chip
            icon={<VerifiedIcon />}
            label="Verified"
            color="info"
            size="small"
            sx={{ ml: 1 }}
          />
        </Tooltip>
      );
    }
    if (lead.legitimacyStatus === 'REJECTED') {
      return (
        <Tooltip title="This lead has been rejected multiple times">
          <Chip
            icon={<ThumbDownIcon />}
            label="Rejected"
            color="error"
            size="small"
            sx={{ ml: 1 }}
          />
        </Tooltip>
      );
    }
    return null;
  };

  return (
    <Card
      sx={{
        mb: 2,
        border: 1,
        borderColor: lead.legitimacyStatus === 'CONVERTED' ? 'success.main' :
                     lead.legitimacyStatus === 'VERIFIED' ? 'info.main' : 'divider',
        bgcolor: lead.legitimacyStatus === 'CONVERTED' ? 'success.50' :
                 lead.legitimacyStatus === 'VERIFIED' ? 'info.50' : 'background.paper',
        '&:hover': {
          boxShadow: 3,
          borderColor: 'primary.main'
        },
        transition: 'all 0.2s',
        cursor: 'pointer',
      }}
      onClick={() => onViewDetails(lead)}
    >
      <CardContent sx={{ pb: 1, px: { xs: 2, sm: 2 } }}>
        {/* Mobile Layout */}
        {isMobile ? (
          <Box>
            {/* Top Row: Company Name + Website Link */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, flex: 1 }}>
                {lead.companyName}
              </Typography>
              {lead.website && (
                <IconButton
                  size="small"
                  href={lead.website}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                >
                  <OpenInNewIcon fontSize="small" />
                </IconButton>
              )}
            </Box>

            {/* Badges Row: AI Score + Potential Rating */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, flexWrap: 'wrap' }}>
              {lead.aiScore !== undefined && lead.aiScore !== null && (
                <Chip
                  label={`AI: ${lead.aiScore}%`}
                  size="small"
                  sx={{
                    fontWeight: 600,
                    bgcolor: getScoreColor(lead.aiScore),
                    color: 'white',
                  }}
                />
              )}
              {lead.potentialRating && (
                <Chip
                  label={lead.potentialRating.replace('_', ' ')}
                  color={getPotentialColor(lead.potentialRating)}
                  size="small"
                  icon={<StarIcon sx={{ fontSize: 14 }} />}
                  sx={{ fontWeight: 600, '& .MuiChip-icon': { ml: 0.5 } }}
                />
              )}
              {getLegitimacyBadge()}
            </Box>

            {/* Info Row: Industry + Location + Contacts */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              {lead.industry && (
                <Chip
                  label={lead.industry}
                  size="small"
                  variant="outlined"
                  sx={{ height: 24 }}
                />
              )}
              {lead.location && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <LocationIcon sx={{ fontSize: 16 }} color="action" />
                  <Typography variant="body2" color="text.secondary">
                    {lead.location}
                  </Typography>
                </Box>
              )}
              {contactCount > 0 && (
                <Chip
                  icon={<PeopleIcon sx={{ fontSize: 14 }} />}
                  label={`${contactCount} contact${contactCount > 1 ? 's' : ''}`}
                  size="small"
                  color="info"
                  variant="outlined"
                  sx={{ height: 24, '& .MuiChip-icon': { ml: 0.5 } }}
                />
              )}
            </Box>
          </Box>
        ) : (
          /* Desktop Layout - Original */
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
            {/* Company Avatar */}
            <Avatar sx={{ width: 48, height: 48, bgcolor: 'primary.main' }}>
              <BusinessIcon />
            </Avatar>

            {/* Company Info */}
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1, minWidth: 0 }}>
                  {lead.companyName}
                </Typography>
                {getLegitimacyBadge()}
                {lead.website && (
                  <IconButton
                    size="small"
                    href={lead.website?.startsWith('http') ? lead.website : `https://${lead.website}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    sx={{ flexShrink: 0 }}
                  >
                    <OpenInNewIcon fontSize="small" />
                  </IconButton>
                )}
              </Box>

              {/* Quick Info Row */}
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                useFlexGap
                sx={{ flexWrap: 'wrap', rowGap: 0.5 }}
              >
                {lead.industry && (
                  <Chip
                    label={lead.industry}
                    size="small"
                    variant="outlined"
                    sx={{ height: 24, flexShrink: 0 }}
                  />
                )}
                {lead.location && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0, flexShrink: 1 }}>
                    <LocationIcon fontSize="small" color="action" sx={{ fontSize: 16, flexShrink: 0 }} />
                    <Typography variant="body2" color="text.secondary" noWrap sx={{ minWidth: 0 }}>
                      {lead.location}
                    </Typography>
                  </Box>
                )}
                {contactCount > 0 && (
                  <Chip
                    icon={<PeopleIcon sx={{ fontSize: 14 }} />}
                    label={contactCount}
                    size="small"
                    color="info"
                    variant="outlined"
                    sx={{ height: 24, flexShrink: 0, '& .MuiChip-icon': { ml: 0.5 } }}
                  />
                )}
              </Stack>
            </Box>

            {/* AI Score Badge */}
            {lead.aiScore !== undefined && lead.aiScore !== null && (
              <Box sx={{ textAlign: 'center', minWidth: 60 }}>
                <Typography variant="caption" color="text.secondary" display="block">
                  AI Score
                </Typography>
                <Chip
                  label={`${lead.aiScore}%`}
                  size="small"
                  sx={{
                    fontWeight: 600,
                    bgcolor: getScoreColor(lead.aiScore),
                    color: 'white',
                  }}
                />
              </Box>
            )}

            {/* Potential Rating */}
            {lead.potentialRating && (
              <Chip
                label={lead.potentialRating.replace('_', ' ')}
                color={getPotentialColor(lead.potentialRating)}
                size="small"
                icon={<StarIcon />}
                sx={{ fontWeight: 600 }}
              />
            )}
          </Box>
        )}
      </CardContent>

      {/* Enrichment status indicator */}
      {lead.enrichmentStatus === 'PENDING' && contactCount === 0 && (
        <Box sx={{ px: 2, pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
            <Typography variant="caption" color="text.secondary">
              Finding contacts...
            </Typography>
          </Box>
          <LinearProgress sx={{ borderRadius: 1, height: 3 }} />
        </Box>
      )}
      {lead.enrichmentStatus === 'IN_PROGRESS' && contactCount === 0 && (
        <Box sx={{ px: 2, pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
            <Typography variant="caption" color="info.main" fontWeight={500}>
              Enriching contacts...
            </Typography>
          </Box>
          <LinearProgress color="info" sx={{ borderRadius: 1, height: 3 }} />
        </Box>
      )}
      {lead.enrichmentStatus === 'FAILED' && contactCount === 0 && (
        <Box sx={{ px: 2, pb: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="caption" color="text.secondary">
            Could not find contacts
          </Typography>
          {onRetryEnrich && (
            <Button
              size="small"
              startIcon={<RetryIcon sx={{ fontSize: 14 }} />}
              onClick={(e) => {
                e.stopPropagation();
                onRetryEnrich(lead);
              }}
              sx={{ textTransform: 'none', fontSize: '0.75rem', py: 0 }}
            >
              Retry
            </Button>
          )}
        </Box>
      )}

      <CardActions sx={{ px: 2, pb: 2, pt: 0, flexDirection: isMobile ? 'column' : 'row', gap: 1 }}>
        <Button
          variant="outlined"
          size="small"
          startIcon={<ViewIcon />}
          onClick={(e) => {
            e.stopPropagation();
            onViewDetails(lead);
          }}
          fullWidth={isMobile}
          sx={{ flex: isMobile ? undefined : 1 }}
        >
          View Details (查看詳情)
        </Button>
        {lead.id && <AssignLeadMenu generatedLeadId={lead.id} />}
        {/* Quick Add hidden — contact details are masked until revealed in View Details */}
      </CardActions>
    </Card>
  );
};

export default GeneratedLeadCard;
